/*
 * Copyright (c) CIRROM 2024.
 */
import { NewCRMPanelBarSection } from "./CRMPanelBarSection";
import React, { useState } from "react";
import { a, useSpring } from "@react-spring/web";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { NUMBER_FORMATTER } from "../utils/utils";

const ChangePanelBarSection = (
  props: Readonly<{
    label: string;
    showApproved: boolean;
    changeMetrics: any;
    series?: string[];
    alternativeChartVariant?: boolean;
    maxHeight?: number;
  }>
) => {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant={"h3"} style={{ fontWeight: "bold" }} gutterBottom>
          {props.showApproved
            ? `${props.label} Changes`
            : `${props.label} Rejected Changes`}
        </Typography>
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-wrap", fontSize: "medium" }}>
              <Typography variant={"caption"}>
                Total approved:
                {NUMBER_FORMATTER.format(
                  props.changeMetrics.reduce(
                    (prev, item) => prev + item.approved_total,
                    0
                  )
                )}
              </Typography>

              <br />
              <Typography variant={"caption"}>
                Total rejected:
                {NUMBER_FORMATTER.format(
                  props.changeMetrics.reduce(
                    (prev, item) => prev + item.rejected_total,
                    0
                  )
                )}
              </Typography>
            </div>
          }
        >
          <Typography
            variant={"h4"}
            style={{
              fontWeight: "bold",
              alignSelf: "baseline",
              color: "white",
              backgroundColor: "#0090C3",
              borderRadius: "13px",
              padding: "3px 16px",
            }}
          >
            {NUMBER_FORMATTER.format(
              props.changeMetrics.reduce(
                (prev, item) =>
                  props.showApproved
                    ? prev + item.approved_total
                    : prev + item.rejected_total,
                0
              )
            )}
          </Typography>
        </Tooltip>
      </div>
      <NewCRMPanelBarSection
        changeMetrics={props.changeMetrics}
        series={props.series}
        alternativeChartVariant={props.alternativeChartVariant}
        maxHeight={props.maxHeight}
      />
    </>
  );
};

export function CRMChangeStats(
  props: Readonly<{
    label: string;
    changeMetrics: any;
    classes: any;
    maxChartHeight?: number;
  }>
) {
  /**
   * Panel displaying the CRM Change Stats
   *
   * */
  const [displayApprovedChanges, setDisplayApprovedChanges] =
    useState<boolean>(true);
  const { transform, opacity } = useSpring({
    opacity: displayApprovedChanges ? 1 : 0,
    transform: `perspective(600px) rotateX(${
      displayApprovedChanges ? 180 : 0
    }deg)`,

    config: { mass: 5, tension: 250, friction: 50 },
  });

  return (
    <>
      <a.div
        style={{
          opacity: opacity,
          transform,
          rotateX: "180deg",
          display: opacity.to((o) => `${o < 0.65 ? "none" : "inherit"}`),
        }}
        className={props.classes}
      >
        <ChangePanelBarSection
          label={props.label}
          showApproved={true}
          changeMetrics={props.changeMetrics}
          series={["approved_total"]}
          maxHeight={props.maxChartHeight}
        />
        <Typography
          variant={"h5"}
          style={{
            fontWeight: "bold",
            color: "#777776",
            cursor: "pointer",
            marginTop: "5px",
          }}
          onClick={() => setDisplayApprovedChanges(false)}
        >
          View More
        </Typography>
      </a.div>
      <a.div
        style={{
          opacity: opacity.to((o) => 1 - o),
          transform,
          display: opacity.to((o) => `${o >= 0.65 ? "none" : "inherit"}`),
        }}
        className={props.classes}
      >
        <ChangePanelBarSection
          label={props.label}
          showApproved={false}
          changeMetrics={props.changeMetrics}
          series={["rejected_total"]}
          maxHeight={props.maxChartHeight}
        />
        <Typography
          variant={"h5"}
          style={{
            fontWeight: "bold",
            color: "#777776",
            cursor: "pointer",
          }}
          onClick={() => setDisplayApprovedChanges(true)}
        >
          View Approved Changes
        </Typography>
      </a.div>
    </>
  );
}
